// Custom hook qui encapsule la génération PDF côté frontend (jsPDF + html2canvas).
// Extracted from SyntheseMarchesPage.js. The hook owns the `generatingPdf` flag
// and exposes a single async `downloadPdf` action.
import { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { toast } from 'sonner';
import { trackPdfDownload } from '../../utils/analytics';

export function usePdfExport({ resultRef, fileName, domain, documentType }) {
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const downloadPdf = async () => {
    if (!resultRef.current) {
      toast.error("Aucune analyse à exporter");
      return;
    }
    setGeneratingPdf(true);
    try {
      // Temporarily remove scroll constraints so html2canvas captures the FULL content
      const el = resultRef.current;
      const originalMaxHeight = el.style.maxHeight;
      const originalOverflow = el.style.overflow;
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible';

      await new Promise((r) => requestAnimationFrame(() => r()));

      let canvas;
      try {
        canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          windowWidth: el.scrollWidth,
          windowHeight: el.scrollHeight,
        });
      } finally {
        el.style.maxHeight = originalMaxHeight;
        el.style.overflow = originalOverflow;
      }

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const contentWidth = pdfWidth - margin * 2;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Header on first page
      pdf.setFillColor(124, 58, 237);
      pdf.rect(0, 0, pdfWidth, 22, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(14);
      pdf.text('Mon DCE Express - Synthèse IA', margin, 10);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.text(
        `${documentType.toUpperCase()} - ${domain} - Mode détaillé`,
        margin,
        17
      );
      pdf.setFontSize(8);
      const today = new Date().toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
      pdf.text(`Généré le ${today}`, pdfWidth - margin - 35, 17);

      // Document name
      pdf.setTextColor(60, 60, 60);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'italic');
      const truncatedName =
        fileName.length > 80 ? fileName.substring(0, 77) + '...' : fileName;
      pdf.text(`Document analysé : ${truncatedName}`, margin, 30);

      // Slice the canvas into A4 pages
      const contentStartY = 36;
      const usableHeight = pdfHeight - contentStartY - margin;
      let remainingHeight = imgHeight;
      let position = 0;
      let pageNum = 1;

      while (remainingHeight > 0) {
        const heightOnThisPage = Math.min(remainingHeight, usableHeight);
        const srcY = (position * canvas.width) / contentWidth;
        const srcH = (heightOnThisPage * canvas.width) / contentWidth;

        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        const ctx = sliceCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL('image/png');

        if (pageNum > 1) {
          pdf.addPage();
          pdf.setFillColor(124, 58, 237);
          pdf.rect(0, 0, pdfWidth, 8, 'F');
          pdf.setTextColor(255, 255, 255);
          pdf.setFontSize(8);
          pdf.text('Mon DCE Express - Synthèse IA (suite)', margin, 5.5);
        }

        const yStart = pageNum === 1 ? contentStartY : 12;
        pdf.addImage(sliceData, 'PNG', margin, yStart, contentWidth, heightOnThisPage);

        // Footer
        pdf.setTextColor(150, 150, 150);
        pdf.setFontSize(7);
        pdf.text(
          `Page ${pageNum} - dce-express.fr - Synthèse IA confidentielle`,
          margin,
          pdfHeight - 5
        );

        remainingHeight -= heightOnThisPage;
        position += heightOnThisPage;
        pageNum += 1;
      }

      const safeName = (fileName || 'synthese')
        .replace(/\.pdf$/i, '')
        .replace(/[^a-zA-Z0-9_-]/g, '_');
      pdf.save(`Synthese-IA_${safeName}.pdf`);
      trackPdfDownload({ domain, documentType });
      toast.success('PDF téléchargé avec succès');
    } catch (e) {
      console.error('PDF generation error:', e);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  return { generatingPdf, downloadPdf };
}
